import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import MainEditor from "./MugEditorFeature/EditorComponents/MainEditor";
import Home from "./Pages/Home";
import Inspiration from "./Pages/Inspiration";
import CaseConfiguration from "./Pages/CaseConfiguration";
import OrderSummary from "./Pages/OrderSummary";
import PaymentSuccess from "./Pages/PaymentSuccess";
import { useStickerSectionStore } from './MugEditorFeature/EditorStore';
import { useEffect, useRef, useState } from 'react';
import localforage from 'localforage';
import { CanvasDrawer } from './MugEditorFeature/CanvasDrawer';
import { UndoRedoHelper } from './MugEditorFeature/UndoRedoHelper';
import PrivacyPolicy from './Pages/PrivacyPolicy';
import TermsAndConditions from './Pages/TermsAndConditions';
import PreviewPage from './Pages/PreviewPage';
import { EditorCacheManager } from './MugEditorFeature/EditorCacheManager';
import { ImageStore } from './MugEditorFeature/ImageStore';
import { v4 as uuidv4 } from 'uuid';

function App() {
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(true);
  const phoneModel = useStickerSectionStore((state) => state.phoneModel);
  const setPhoneModel = useStickerSectionStore((state) => state.setPhoneModel);
  const setShowBackgroundImage = useStickerSectionStore(state => state.setShowBackgroundImage);
  const setBackgroundBitmap = useStickerSectionStore(state => state.setBackgroundBitmap);
  const setBackgroundColor = useStickerSectionStore(state => state.setBackgroundColor);
  const setCaseFinish = useStickerSectionStore((state) => state.setCaseFinish)
  const setMagSafe = useStickerSectionStore((state) => state.setMagSafe)
  const setTutorialToolTipShown = useStickerSectionStore((state) => state.setTutorialToolTipShown)
  const setUuid = useStickerSectionStore((state) => state.setUuid)
  const setGhostsEnabled = useStickerSectionStore((state) => state.setGhostsEnabled)
  const setGhostSize = useStickerSectionStore((state) => state.setGhostSize)
  const setGhostFrequency = useStickerSectionStore((state) => state.setGhostFreq)
  const setGhostOpacity = useStickerSectionStore((state) => state.setGhostOpacity)
  const setIsPortrait = useStickerSectionStore((state) => state.setIsPortrait)

  const state = useStickerSectionStore.getState();

  const cacheInitialized = useRef(false)

  useEffect(() => {

    async function initialize() {
      setIsPortrait(window.matchMedia("(orientation: portrait)").matches)

      CanvasDrawer.initCDStates()

      await ImageStore.init()

      const cacheName = "image-cache";
      const cache = await caches.open(cacheName)

      // load cache
      const [
        bgImageResponse,
        showBackgroundImage,
        backgroundColor,
        caseFinish,
        magSafe,
        ghostsEnabled,
        ghostSize,
        ghostFreq,
        ghostOpacity,
        uuid,
        phoneModel
      ] = await Promise.all([
        cache.match("backgroundImage"),
        localforage.getItem<boolean>("showBackgroundImage"),
        localforage.getItem<string>("backgroundColor"),
        localforage.getItem<string>("caseFinish"),
        localforage.getItem<boolean>("magSafe"),
        localforage.getItem<boolean>("ghostsEnabled"),
        localforage.getItem<number>("ghostSize"),
        localforage.getItem<number>("ghostFreq"),
        localforage.getItem<number>("ghostOpacity"),
        localforage.getItem<string>('uuid'),
        localforage.getItem<string>('phoneModel')
      ]);

      if (bgImageResponse) {
        const blob = await bgImageResponse.blob()
        const backgroundBitmap = await createImageBitmap(blob)
        setBackgroundBitmap(backgroundBitmap)
        CanvasDrawer.BackgroundImageBitmap = backgroundBitmap
      }

      setShowBackgroundImage(showBackgroundImage ?? state.showBackgroundImage)
      setBackgroundColor(backgroundColor ?? state.backgroundColor)
      setCaseFinish(caseFinish ?? state.caseFinish)
      setMagSafe(magSafe ?? state.magSafe)
      setGhostsEnabled(ghostsEnabled ?? state.ghostsEnabled)
      setGhostSize(ghostSize ?? state.ghostSize)
      setGhostFrequency(ghostFreq ?? state.ghostFreq)
      setGhostOpacity(ghostOpacity ?? state.ghostOpacity)

      const finalUuid = uuid ?? uuidv4()
      setUuid(finalUuid)

      if (!uuid) {
        await localforage.setItem('uuid', finalUuid)
      }

      if (phoneModel !== null) {
        setPhoneModel(phoneModel);
        setTutorialToolTipShown(true);
      }

      setIsBackgroundLoading(false);

      await EditorCacheManager.getCanvasImagesFromCache()

      EditorCacheManager.subscribeStore()
      cacheInitialized.current = true
      EditorCacheManager.startAutoSave()
      CanvasDrawer.allocateGhostArrayImages()

      UndoRedoHelper.createSnapShot();

      return () => {
        EditorCacheManager.stopAutoSave()
      }
    }
    if (!cacheInitialized.current) {
      initialize();
    }

  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/editor" element={protectedRoute(<MainEditor />, phoneModel, isBackgroundLoading)} />
        <Route path="/inspiration" element={<Inspiration />} />
        <Route path="/caseconfiguration" element={<CaseConfiguration />} />
        <Route path="/order-summary" element={<OrderSummary />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path='/privacypolicy' element={<PrivacyPolicy />} />
        <Route path='/termsandconditions' element={<TermsAndConditions />} />
        <Route path='/preview' element={<PreviewPage />} />
      </Routes>

      <link
        href="https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;600;700&display=swap"
        rel="stylesheet"
      />

    </Router>
  );
}

export default App;



const protectedRoute = (
  route: React.ReactNode,
  templateImagePath: string | null,
  isLoading: boolean
) => {
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: "100%",
        flexDirection: 'column',
        backgroundColor: "#7a9a9a"
      }}>
        <div style={{
          fontSize: '24px',
          marginBottom: '20px',
          color: '#f4f8f8'
        }}>Loading</div>
        <div style={{
          width: '50px',
          height: '50px',
          border: "3px solid #5a7a7a",
          borderTop: "3px solid #6b9e9e",
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (templateImagePath) {
    return route;
  } else {
    return <Navigate to="/" />;
  }
};



